import { styled } from "@mui/material/styles";
import { useMemo, type ReactNode } from "react";

import { GAP, HEX_H, HEX_W, ROW_STRIDE } from "./geometry";
import { HoneycombBackground, TILE_H, TILE_W } from "./HoneycombBackground";
import { useHoneycombFit } from "./useHoneycombFit";
import { DRAGON_TILES_LAYOUT_VARIANTS } from "../../constants";

const ACTIVE_PADDING = 0.1;
const SCALE_MIN = 0.75;
const SCALE_MAX = 1.2;
const TILE_SIZE = { width: TILE_W, height: TILE_H };

const STEP_X = HEX_W + GAP;
const DRAGON_TILE_LAYOUTS = DRAGON_TILES_LAYOUT_VARIANTS as Record<
  number,
  readonly number[]
>;

interface HexPosition {
  x: number;
  y: number;
}

interface HoneycombLayout {
  positions: HexPosition[];
  bounds: { width: number; height: number };
  firstActive: HexPosition | undefined;
}

function rowHasCenterHex(rowIdx: number): boolean {
  // Top row (idx 0) has a center anchor.
  return rowIdx % 2 === 0;
}

function getLeftToRightRowOffsets(
  count: number,
  hasCenterHex: boolean,
): number[] {
  if (count <= 0) {
    return [];
  }

  // Left-to-right order so visual placement matches tile index/order.
  // Bias incomplete rows slightly to the left to avoid right-heavy placement.
  const start = hasCenterHex ? -Math.floor(count / 2) : -Math.ceil(count / 2);

  return Array.from({ length: count }, (_, index) => start + index);
}

// Pick a stable top-left active tile to phase-align the repeating background honeycomb.
function pickTopLeftPosition(
  positions: HexPosition[],
): HexPosition | undefined {
  return positions.reduce<HexPosition | undefined>((currentBest, candidate) => {
    if (!currentBest) return candidate;
    if (candidate.y < currentBest.y) return candidate;
    if (candidate.y > currentBest.y) return currentBest;
    return candidate.x < currentBest.x ? candidate : currentBest;
  }, undefined);
}

function buildHoneycombLayout(rowCounts: readonly number[]): HoneycombLayout {
  let minX = Infinity;
  let maxX = -Infinity;
  const rawPositions: HexPosition[] = [];

  rowCounts.forEach((count, rowIdx) => {
    const hasCenterHex = rowHasCenterHex(rowIdx);
    const rowBaseOffset = hasCenterHex ? 0 : 0.5;
    const rowOffsets = getLeftToRightRowOffsets(count, hasCenterHex);

    rowOffsets.forEach((offset) => {
      const x = (rowBaseOffset + offset) * STEP_X;
      const y = rowIdx * ROW_STRIDE;
      rawPositions.push({ x, y });
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
    });
  });

  if (rawPositions.length === 0) {
    return {
      positions: [],
      bounds: { width: 0, height: 0 },
      firstActive: undefined,
    };
  }

  const rawWidth = maxX - minX + HEX_W;
  const rawHeight = (rowCounts.length - 1) * ROW_STRIDE + HEX_H;
  const paddedWidth = rawWidth * (1 + ACTIVE_PADDING);
  const paddedHeight = rawHeight * (1 + ACTIVE_PADDING);
  const offsetX = (paddedWidth - rawWidth) / 2 - minX;
  const offsetY = (paddedHeight - rawHeight) / 2;

  const positions = rawPositions.map(({ x, y }) => ({
    x: x + offsetX,
    y: y + offsetY,
  }));

  return {
    positions,
    bounds: { width: paddedWidth, height: paddedHeight },
    firstActive: pickTopLeftPosition(positions),
  };
}

const Fitter = styled("div")`
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const Sizer = styled("div")`
  margin: auto;
  flex-shrink: 0;
`;

const Scaler = styled("div")`
  position: relative;
  transform-origin: top left;
`;

const HexAnchor = styled("div")`
  position: absolute;
  width: ${HEX_W}px;
  height: ${HEX_H}px;
`;

interface HoneycombGridProps<T> {
  tiles: T[];
  rowCounts?: readonly number[];
  renderTile: (tile: T, index: number) => ReactNode;
  className?: string;
}

export function HoneycombGrid<T>({
  tiles,
  rowCounts,
  renderTile,
  className,
}: HoneycombGridProps<T>) {
  const resolvedRowCounts = useMemo(() => {
    if (rowCounts) {
      return rowCounts;
    }
    if (tiles.length <= 0) {
      return [0, 0, 0, 0] as const;
    }
    const clampedCount = Math.min(20, tiles.length);
    const variant = DRAGON_TILE_LAYOUTS[clampedCount];
    if (!variant) {
      throw new Error(
        `Missing honeycomb row layout variant for tile count ${clampedCount}.`,
      );
    }
    return variant;
  }, [rowCounts, tiles.length]);

  const layout = useMemo(
    () => buildHoneycombLayout(resolvedRowCounts),
    [resolvedRowCounts],
  );

  const { fitterRef, bgRef, sizerRef, scalerRef } = useHoneycombFit({
    bounds: layout.bounds,
    firstActive: layout.firstActive,
    tile: TILE_SIZE,
    scaleMin: SCALE_MIN,
    scaleMax: SCALE_MAX,
  });

  return (
    <Fitter ref={fitterRef} className={className ?? "honeycomb-grid"}>
      <HoneycombBackground ref={bgRef}>
        <Sizer ref={sizerRef}>
          <Scaler
            ref={scalerRef}
            style={{ width: layout.bounds.width, height: layout.bounds.height }}
          >
            {layout.positions.map((pos, i) => {
              const tile = tiles[i];
              if (!tile) return null;
              return (
                <HexAnchor
                  key={i}
                  style={{
                    left: `${pos.x}px`,
                    top: `${pos.y}px`,
                  }}
                >
                  {renderTile(tile, i)}
                </HexAnchor>
              );
            })}
          </Scaler>
        </Sizer>
      </HoneycombBackground>
    </Fitter>
  );
}
